import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, useToast } from '@vd/ui';
import { BookOpen, Plus, Trash2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { useAuth } from '../../../features/auth/AuthProvider';
import { CaptureProfileModal } from '../../../features/modals/CaptureProfileModal';
import { CaptureProfilesHelpModal } from '../../../features/modals/CaptureProfilesHelpModal';
import { patternLabel } from '../../../features/modals/captureProfilePattern';
import { DestructiveConfirm } from '../../../features/modals/DestructiveConfirm';
import styles from '../Settings.module.css';

type ServerProfile = {
  id: string;
  name: string;
  originPattern: string;
  mode: 'full' | 'metadata_only' | 'never';
  extraRedactions: { headers?: string[]; urlParams?: string[] };
};

/**
 * The form keeps one list of extra redactions, labelled header / query-param.
 * A new name is both: it was sent as a header alone, so a query parameter
 * named there was never redacted from a URL. A name the profile already had
 * keeps the kind it was stored as, so saving an unrelated change does not
 * widen redaction nobody asked to widen.
 */
export function splitExtras(names: string[], stored?: { headers?: string[]; urlParams?: string[] }) {
  // By name, not spelling: both redactors compare names case-insensitively, so
  // a stored `Session` is the `session` somebody types again.
  const key = (name: string) => name.trim().toLowerCase();
  const headers = new Set((stored?.headers ?? []).map(key));
  const urlParams = new Set((stored?.urlParams ?? []).map(key));
  const known = (name: string) => headers.has(key(name)) || urlParams.has(key(name));
  return {
    headers: names.filter((name) => !known(name) || headers.has(key(name))),
    urlParams: names.filter((name) => !known(name) || urlParams.has(key(name))),
  };
}

/** One entry per name, in its first spelling: the redactors ignore case. */
function uniqueNames(names: string[]): string[] {
  const seen = new Set<string>();
  return names.filter((name) => {
    const key = name.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function CaptureProfilesTab() {
  const { currentCompanyId } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [editing, setEditing] = useState<ServerProfile | null>(null);
  const [deleting, setDeleting] = useState<ServerProfile | null>(null);
  // A fresh editor for each open: it reads its fields from `initial` once, so a
  // kept instance showed the previous profile's values.
  const [editorKey, setEditorKey] = useState(0);

  const { data: profiles = [] } = useQuery({
    queryKey: ['capture-profiles', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listCaptureProfiles(currentCompanyId) : Promise.resolve([]),
    enabled: !!currentCompanyId,
  });

  const openCreate = () => { setEditing(null); setEditorKey((k) => k + 1); setOpen(true); };
  const openEdit = (p: ServerProfile) => { setEditing(p); setEditorKey((k) => k + 1); setOpen(true); };

  const handleSave = async (data: { originPattern: string; mode: 'full' | 'metadata_only' | 'never'; extraRedactions: { headers?: string[]; urlParams?: string[] }; name?: string }) => {
    if (!currentCompanyId) return;
    const name = data.name || editing?.name || data.originPattern;
    try {
      if (editing) {
        await api.updateCaptureProfile(currentCompanyId, editing.id, {
          name, originPattern: data.originPattern, mode: data.mode, extraRedactions: data.extraRedactions,
        });
        toast.push({ variant: 'success', title: 'Profile updated', description: name });
      } else {
        await api.createCaptureProfile(currentCompanyId, {
          name, originPattern: data.originPattern, mode: data.mode, extraRedactions: data.extraRedactions,
        });
        toast.push({ variant: 'success', title: 'Profile created', description: name });
      }
      await qc.invalidateQueries({ queryKey: ['capture-profiles', currentCompanyId] });
      setOpen(false);
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    }
  };

  const handleDelete = async () => {
    if (!currentCompanyId || !deleting) return;
    try {
      await api.deleteCaptureProfile(currentCompanyId, deleting.id);
      await qc.invalidateQueries({ queryKey: ['capture-profiles', currentCompanyId] });
      toast.push({ variant: 'info', title: 'Profile deleted', description: deleting.name });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Delete failed', description: (e as Error).message });
    } finally {
      setDeleting(null);
    }
  };

  const modeLabel = (m: ServerProfile['mode']) =>
    m === 'full' ? 'Full' : m === 'metadata_only' ? 'Metadata only' : 'Never capture';
  const modeBadge = (m: ServerProfile['mode']): 'success' | 'warning' | 'danger' =>
    m === 'full' ? 'success' : m === 'metadata_only' ? 'warning' : 'danger';

  return (
    <>
      <div className={styles.rowSpread}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <h1 className={styles.h1} style={{ marginBottom: 0 }}>Capture profiles</h1>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 13,
                color: 'var(--accent)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                textDecoration: 'underline',
              }}
            >
              <BookOpen size={12} />
              Learn how this works
            </button>
          </div>
          <p className={styles.sub} style={{ marginTop: 8 }}>
            Per-origin rules for what the browser extension captures and
            what gets redacted before storage.
          </p>
        </div>
        <Button variant="primary" leftIcon={<Plus size={14} />} onClick={openCreate} style={{ flexShrink: 0 }}>
          New profile
        </Button>
      </div>
      {/* Scrolls on its own at a narrow width, rather than pushing the page sideways. */}
      <section className={`${styles.section} ${styles.tableScroll}`}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th><th>Origin pattern</th><th>Mode</th><th>Extra redactions</th><th style={{ width: 140 }}></th>
            </tr>
          </thead>
          <tbody>
            {profiles.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>
                No capture profiles yet. Captures on unknown origins default to full capture.
              </td></tr>
            ) : profiles.map((p) => {
              const extras = [
                ...(p.extraRedactions.headers ?? []).map((h) => `${h} (header)`),
                ...(p.extraRedactions.urlParams ?? []).map((u) => `${u} (url)`),
              ];
              return (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                    {p.originPattern}
                    {(() => {
                      const label = patternLabel(p.originPattern);
                      return label ? (
                        <span style={{ marginLeft: 8, fontFamily: 'inherit' }}>
                          <Badge variant={label.tone === 'danger' ? 'danger' : label.tone === 'warning' ? 'warning' : 'outline'}>{label.text}</Badge>
                        </span>
                      ) : null;
                    })()}
                  </td>
                  <td><Badge variant={modeBadge(p.mode)}>{modeLabel(p.mode)}</Badge></td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    {extras.length === 0 ? 'None' : extras.join(', ')}
                  </td>
                  <td style={{ display: 'flex', gap: 4 }}>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>Edit</Button>
                    <Button variant="ghost" size="sm" leftIcon={<Trash2 size={14} />} onClick={() => setDeleting(p)}>Delete</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <CaptureProfileModal
        key={editorKey}
        open={open}
        onClose={() => setOpen(false)}
        initial={editing ? {
          originPattern: editing.originPattern,
          mode: editing.mode === 'metadata_only' ? 'metadata' : editing.mode,
          extraDenyList: uniqueNames([
            ...(editing.extraRedactions.headers ?? []),
            ...(editing.extraRedactions.urlParams ?? []),
          ]),
        } : undefined}
        title={editing ? 'Edit capture profile' : 'New capture profile'}
        onSave={(data) => handleSave({
          originPattern: data.originPattern,
          mode: data.mode === 'metadata' ? 'metadata_only' : data.mode,
          extraRedactions: splitExtras(data.extraDenyList, editing?.extraRedactions),
        })}
      />

      <DestructiveConfirm
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={`Delete ${deleting?.name ?? 'profile'}?`}
        description="Captures from this origin will fall back to the default (full capture) until a new profile matches."
        confirmLabel="Delete"
      />

      <CaptureProfilesHelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </>
  );
}
